import { tellTales } from "@/constants/TellTales";

/** What the point details showed before they could be chosen, and still show until someone does. */
export const DEFAULT_POINT_DETAIL_IDS = [
  "DiagnosticEngineSpeedId",
  "DiagnosticIgnitionId",
  "DiagnosticEngineRoadSpeedId",
  "DiagnosticFuelLevelId",
  "DiagnosticDieselExhaustFluidId",
  "DiagnosticStateOfChargeId",
];

/** Diagnostics that are only ever on or off, shown as yes or no. */
export const BOOLEAN_DIAGNOSTIC_IDS = new Set(["DiagnosticIgnitionId"]);

const TELLTALE_ISO = new Map(tellTales.map(tellTale => [tellTale.id, tellTale.iso]));

const TELLTALE_PREFIX = /^(TellTale|Warnleuchte|Kontrollleuchte)/i;

/** Warning lamps on the dashboard, offered apart from the engine values. */
export function isTellTale(id: string, name: string | undefined): boolean {
  return TELLTALE_ISO.has(id) || TELLTALE_PREFIX.test(name ?? "");
}

/** The ISO 7000 symbol of a known lamp, for its icon. */
export function tellTaleIso(id: string): string | undefined {
  return TELLTALE_ISO.get(id);
}

/** Red, yellow or information. Off (0), reserved and not available are not lit. */
export function isLitTellTale(value: number): boolean {
  return value >= 1 && value <= 3;
}

/** "TellTale – Warning (0 = off …)" → "Warning": beside its symbol the prefix says nothing. */
export function tellTaleLabel(name: string | undefined, fallback: string): string {
  return shortName(name, fallback).replace(/^(TellTale|Warnleuchte|Kontrollleuchte)\s*[–-]\s*/i, "") || fallback;
}

const VALUE_LIST = /\(([^()]*\d+\s*[=.]\s*[^\d\s][^()]*)\)\s*$/;

/**
 * The meaning of each value where the name lists them, as Geotab's tell-tale names do — in two
 * spellings: "TellTale – Warning (0 = off / 1 = red …)" in our list, "(0. Off, 1. Red, …)" in the
 * databases.
 */
export function valueLabels(name: string | undefined): Map<number, string> | null {
  const list = VALUE_LIST.exec(name ?? "")?.[1];
  if (!list) return null;
  const labels = new Map<number, string>();
  for (const part of list.split(/[/,]/)) {
    const match = /^\s*(\d+)\s*[=.]\s*(.+?)\s*$/.exec(part);
    if (match) labels.set(Number(match[1]), match[2]);
  }
  return labels.size > 1 ? labels : null;
}

/** The name without the list of value meanings, which would not fit beside the value. */
export function shortName(name: string | undefined, fallback: string): string {
  const trimmed = (name ?? "").replace(VALUE_LIST, "").trim();
  return trimmed || fallback;
}
